import { useOutletContext } from 'react-router-dom';
import type { AuthUser } from '../../hooks/useAuth';
import { Building2, Package, FileText, DollarSign, Truck, Sparkles, MapPin, Inbox, Wrench, Plus, History, Construction } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface QuickAccessItem {
  icon: LucideIcon;
  label: string;
  color: string;
}

const ROLE_CONFIG = {
  broker: {
    label:       'Cliente',
    color:       'text-broker',
    bg:          'bg-broker-bg dark:bg-broker-dark-bg',
    border:      'border-broker-border dark:border-broker-dark-border',
    icon:        Building2,
    welcome:     'Tablero de Transporte',
    description: 'Aquí verás las cargas disponibles, tus ofertas recibidas y podrás publicar nuevas cargas.',
    nextLabel:   'Próximo módulo: Tablero de Transporte',
    items: [
      { icon: Package,    label: 'Publicar carga',       color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: FileText,   label: 'Mis cargas activas',   color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: DollarSign, label: 'Ofertas recibidas',    color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: Truck,      label: 'Camiones disponibles', color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
    ] as QuickAccessItem[],
  },
  carrier: {
    label:       'Transportista',
    color:       'text-carrier',
    bg:          'bg-carrier-bg dark:bg-carrier-dark-bg',
    border:      'border-carrier-border dark:border-carrier-dark-border',
    icon:        Truck,
    welcome:     'Tablero de Transporte',
    description: 'Aquí verás las cargas disponibles, tus coincidencias inteligentes y podrás publicar tu disponibilidad.',
    nextLabel:   'Próximo módulo: Tablero de Transporte',
    items: [
      { icon: Sparkles, label: 'Coincidencias para ti',    color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: Package,  label: 'Todas las cargas',         color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: Truck,    label: 'Publicar disponibilidad',  color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: MapPin,   label: 'Mis rutas frecuentes',     color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
    ] as QuickAccessItem[],
  },
  service_provider: {
    label:       'Proveedor de servicios',
    color:       'text-provider',
    bg:          'bg-provider-bg dark:bg-provider-dark-bg',
    border:      'border-provider-border dark:border-provider-dark-border',
    icon:        Wrench,
    welcome:     'Inicio',
    description: 'Aquí verás las solicitudes de servicio pendientes y podrás gestionar tu catálogo de servicios.',
    nextLabel:   'Próximo módulo: Mis Servicios',
    items: [
      { icon: Inbox,   label: 'Solicitudes pendientes',  color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: Wrench,  label: 'Mis servicios activos',   color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: Plus,    label: 'Publicar servicio',       color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
      { icon: History, label: 'Historial de servicios',  color: 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text' },
    ] as QuickAccessItem[],
  },
} as const;

export function DashboardPage() {
  const user = useOutletContext<AuthUser>();
  const config = ROLE_CONFIG[user.role];
  const RoleIcon = config.icon;

  return (
    <div className="px-4 py-5 max-w-lg mx-auto">

      {/* Bienvenida */}
      <div className={`rounded-xl border p-4 mb-5 ${config.bg} ${config.border}`}>
        <div className="flex items-center gap-3 mb-2">
          <RoleIcon className={`w-8 h-8 ${config.color}`} />
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${config.color}`}>
              {config.label}
            </p>
            <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text">
              Bienvenido, {user.name.split(' ')[0]}
            </h1>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {config.description}
        </p>
      </div>

      {/* Stats dummy */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { val: '3',  label: 'Activas'     },
          { val: '7',  label: 'Pendientes'  },
          { val: '24', label: 'Completadas' },
        ].map(stat => (
          <div key={stat.label} className="bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border shadow-card dark:shadow-none p-3 text-center">
            <p className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-dark-text">{stat.val}</p>
            <p className="text-[11px] font-semibold text-gray-400 dark:text-dark-text-muted uppercase tracking-wide mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Accesos rápidos */}
      <div className="mb-4">
        <h2 className="text-[11px] font-bold text-gray-400 dark:text-dark-text-muted uppercase tracking-wide mb-3">
          Accesos rápidos
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {config.items.map(item => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.label}
                className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all hover:shadow-md hover:dark:shadow-none active:scale-[0.98] ${item.color}`}
              >
                <ItemIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" strokeWidth={2} />
                <span className="text-sm font-semibold leading-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Próximo módulo */}
      <div className="bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border shadow-card dark:shadow-none p-4 flex items-center gap-3">
        <Construction className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        <div>
          <p className="text-sm font-semibold text-gray-700 dark:text-dark-text">{config.nextLabel}</p>
          <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-0.5">En construcción — próxima iteración</p>
        </div>
      </div>

    </div>
  );
}
